import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { motion } from 'framer-motion';
import { ArrowLeft, Trophy, Star, Lock, Lightning, Crown, Fire, Target, CheckCircle, Clock } from '@phosphor-icons/react';
import { useAuth } from '../contexts/AuthContext';
import BottomNav from '../components/BottomNav';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const TIER_COLORS = { free: 'text-[#39FF14]', premium: 'text-[#FACC15]' };

export default function SeasonPassPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [season, setSeason] = useState(null);
  const [progress, setProgress] = useState(null);
  const [leaderboard, setLeaderboard] = useState([]);
  const [activeTab, setActiveTab] = useState('progress');
  const [loading, setLoading] = useState(true);

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    try {
      const [seasonRes, progressRes, lbRes] = await Promise.all([
        axios.get(`${API_URL}/api/season/current`, { withCredentials: true }),
        axios.get(`${API_URL}/api/season/progress`, { withCredentials: true }),
        axios.get(`${API_URL}/api/season/leaderboard`, { withCredentials: true }),
      ]);
      setSeason(seasonRes.data);
      setProgress(progressRes.data);
      setLeaderboard(lbRes.data);
    } catch (err) {
      toast.error('Failed to load season data');
    } finally { setLoading(false); }
  };

  if (loading) return <div className="app-container flex items-center justify-center min-h-screen"><div className="w-10 h-10 border-4 border-[#9D4CDD] border-t-transparent rounded-full animate-spin" /></div>;

  const currentWeek = season?.current_week || 1;
  const weekKey = `week${currentWeek}`;
  const currentChallenges = season?.challenges?.[weekKey] || [];
  const completedIds = progress?.challenges_completed || [];
  const xpProgress = progress?.xp || 0;
  const currentLevel = progress?.level || 1;
  const nextLevel = progress?.next_level;
  const rewards = season?.rewards || [];

  return (
    <div className="app-container" data-testid="season-pass-page">
      <header className="p-6 pb-3">
        <div className="flex items-center gap-3 mb-4">
          <button onClick={() => navigate(-1)} className="w-10 h-10 rounded-full bg-[#181824] flex items-center justify-center" data-testid="season-back-button">
            <ArrowLeft size={20} weight="bold" className="text-white" />
          </button>
          <div>
            <h1 className="text-xl font-bold text-white flex items-center gap-2" style={{ fontFamily: 'Unbounded, sans-serif' }}>
              <Fire size={24} weight="duotone" className="text-[#F97316]" />
              Season Pass
            </h1>
            <p className="text-[#71717A] text-xs">{season?.name}</p>
          </div>
        </div>
      </header>

      <div className="px-6 pb-32 space-y-4">
        {/* Season Progress Bar */}
        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
          className="p-4 rounded-xl bg-gradient-to-br from-[#F97316]/10 to-[#9D4CDD]/5 border border-[#F97316]/20">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2">
              <Star size={18} weight="fill" className="text-[#FACC15]" />
              <span className="text-white text-sm font-bold">Level {currentLevel}</span>
            </div>
            <span className="text-[#A1A1AA] text-xs">{xpProgress} XP</span>
          </div>
          <div className="w-full h-3 bg-[#181824] rounded-full overflow-hidden mb-1">
            <div className="h-full bg-gradient-to-r from-[#F97316] to-[#FACC15] rounded-full transition-all duration-500"
              style={{ width: nextLevel ? `${Math.min(100, (xpProgress / nextLevel.xp_required) * 100)}%` : '100%' }} />
          </div>
          <div className="flex justify-between text-[10px] text-[#71717A]">
            <span>Week {currentWeek} of 4</span>
            <span>{nextLevel ? `${nextLevel.xp_required - xpProgress} XP to Level ${nextLevel.level}` : 'MAX LEVEL'}</span>
          </div>
          {progress?.is_premium && (
            <div className="mt-2 flex items-center gap-1">
              <Crown size={14} weight="fill" className="text-[#FACC15]" />
              <span className="text-[#FACC15] text-[10px] font-bold">PREMIUM PASS ACTIVE</span>
            </div>
          )}
        </motion.div>

        {/* Tabs */}
        <div className="flex gap-2">
          {['progress', 'rewards', 'leaderboard'].map(tab => (
            <button key={tab} onClick={() => setActiveTab(tab)}
              className={`px-4 py-2 rounded-full text-xs font-semibold transition-all ${activeTab === tab ? 'bg-[#9D4CDD] text-white' : 'bg-[#181824] text-[#71717A] hover:text-white'}`}
              data-testid={`season-tab-${tab}`}>
              {tab.charAt(0).toUpperCase() + tab.slice(1)}
            </button>
          ))}
        </div>

        {/* Challenges Tab */}
        {activeTab === 'progress' && (
          <div className="space-y-3" data-testid="season-challenges">
            <h3 className="text-sm font-semibold text-[#A1A1AA]">Week {currentWeek} Challenges</h3>
            {currentChallenges.map((c, i) => {
              const completed = completedIds.includes(c.id);
              const isPremium = c.tier === 'premium';
              return (
                <motion.div key={c.id} initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: i * 0.08 }}
                  className={`p-4 rounded-xl border ${completed ? 'bg-[#39FF14]/5 border-[#39FF14]/20' : isPremium ? 'bg-[#FACC15]/5 border-[#FACC15]/10' : 'bg-[#0F0F16] border-white/5'}`}
                  data-testid={`challenge-${c.id}`}>
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        {completed ? <CheckCircle size={18} weight="fill" className="text-[#39FF14]" /> :
                          isPremium ? <Crown size={18} weight="duotone" className="text-[#FACC15]" /> :
                          <Target size={18} weight="duotone" className="text-[#9D4CDD]" />}
                        <span className="text-white text-sm font-semibold">{c.title}</span>
                        {isPremium && !progress?.is_premium && <Lock size={14} className="text-[#71717A]" />}
                      </div>
                      <p className="text-[#71717A] text-xs ml-7">{c.description}</p>
                    </div>
                    <span className={`text-xs font-bold ${completed ? 'text-[#39FF14]' : TIER_COLORS[c.tier]}`}>
                      +{c.xp_reward} XP
                    </span>
                  </div>
                </motion.div>
              );
            })}
          </div>
        )}

        {/* Rewards Track Tab */}
        {activeTab === 'rewards' && (
          <div className="space-y-2" data-testid="season-rewards">
            <h3 className="text-sm font-semibold text-[#A1A1AA]">Rewards Track</h3>
            {rewards.map((r, i) => {
              const unlocked = xpProgress >= r.xp_required && (r.tier === 'free' || progress?.is_premium);
              const reachable = xpProgress >= r.xp_required;
              return (
                <motion.div key={r.level} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.05 }}
                  className={`p-3 rounded-xl border flex items-center gap-3 ${unlocked ? 'bg-[#39FF14]/5 border-[#39FF14]/20' : reachable && r.tier === 'premium' ? 'bg-[#FACC15]/5 border-[#FACC15]/10' : 'bg-[#0F0F16] border-white/5'}`}
                  data-testid={`reward-level-${r.level}`}>
                  <div className={`w-9 h-9 rounded-full flex items-center justify-center text-xs font-bold ${unlocked ? 'bg-[#39FF14]/20 text-[#39FF14]' : 'bg-[#181824] text-[#71717A]'}`}>
                    {r.level}
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <span className={`text-sm font-medium ${unlocked ? 'text-white' : 'text-[#A1A1AA]'}`}>{r.reward}</span>
                      {r.tier === 'premium' && <Crown size={12} weight="fill" className="text-[#FACC15]" />}
                    </div>
                    <span className="text-[#71717A] text-[10px]">{r.xp_required} XP</span>
                  </div>
                  {unlocked ? <CheckCircle size={18} weight="fill" className="text-[#39FF14]" /> :
                    r.tier === 'premium' && !progress?.is_premium ? <Lock size={16} className="text-[#71717A]" /> : null}
                </motion.div>
              );
            })}
          </div>
        )}

        {/* Leaderboard Tab */}
        {activeTab === 'leaderboard' && (
          <div className="space-y-2" data-testid="season-leaderboard">
            <h3 className="text-sm font-semibold text-[#A1A1AA]">Season Leaderboard</h3>
            {leaderboard.length === 0 ? (
              <div className="text-center py-8">
                <Trophy size={40} weight="duotone" className="text-[#71717A] mx-auto mb-2" />
                <p className="text-[#71717A] text-sm">No entries yet. Complete challenges to climb!</p>
              </div>
            ) : leaderboard.map((entry, i) => (
              <motion.div key={entry.user_id} initial={{ opacity: 0, x: -10 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: i * 0.05 }}
                className={`p-3 rounded-xl border flex items-center gap-3 ${entry.user_id === user?.id ? 'bg-[#9D4CDD]/10 border-[#9D4CDD]/20' : 'bg-[#0F0F16] border-white/5'}`}>
                <span className={`w-7 text-center font-bold text-sm ${i < 3 ? 'text-[#FACC15]' : 'text-[#71717A]'}`}>
                  {i + 1}
                </span>
                <div className="flex-1">
                  <p className="text-white text-sm font-medium">{entry.name}</p>
                  <p className="text-[#71717A] text-[10px]">@{entry.username} · Lv.{entry.level}</p>
                </div>
                <div className="text-right">
                  <p className="text-[#FACC15] text-sm font-bold">{entry.season_xp} XP</p>
                  <p className="text-[#71717A] text-[10px]">{entry.challenges_completed} done</p>
                </div>
              </motion.div>
            ))}
          </div>
        )}

        {/* Upgrade CTA for non-premium — hidden until IAP is wired up (Apple Guideline 3.1.1) */}
        {!progress?.is_premium && process.env.REACT_APP_MONETIZATION_ENABLED === 'true' && (
          <button onClick={() => navigate('/premium')}
            className="w-full p-4 rounded-xl bg-gradient-to-r from-[#FACC15]/10 to-[#F97316]/10 border border-[#FACC15]/20 flex items-center justify-center gap-2 hover:border-[#FACC15]/40 transition-all"
            data-testid="season-upgrade-cta">
            <Crown size={18} weight="fill" className="text-[#FACC15]" />
            <span className="text-[#FACC15] text-sm font-bold">Upgrade to Premium Pass</span>
          </button>
        )}
      </div>

      <BottomNav />
    </div>
  );
}
